import React, { useEffect } from 'react';
import { Select360 } from '../kit/Select360';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Info, X } from 'lucide-react';
import './kit.css';

// Kit DA Happn : 3 composants de base à réutiliser partout (doc : /app/memory/da_kit.md)

// Pulse bref quand le chiffre affiché change (nouvelle facture, nouveau pointage…) pour attirer l'œil.
const usePulseOnChange = (value) => {
  const ref = React.useRef(null);
  const prev = React.useRef(value);
  useEffect(() => {
    if (prev.current !== value && ref.current) {
      const el = ref.current;
      el.classList.remove('kit-flash');
      void el.offsetWidth;
      el.classList.add('kit-flash');
      const t = setTimeout(() => el.classList.remove('kit-flash'), 1500);
      prev.current = value;
      return () => clearTimeout(t);
    }
    prev.current = value;
    return undefined;
  }, [value]);
  return ref;
};

// Filtres : une seule ligne, pills arrondies, actif = noir plein, scroll horizontal (jamais de retour à la ligne).
// item = { key, label, count?, options?: [{key,label}] } — un item avec `options` devient un menu déroulant (filtres moins utilisés).
export const FilterPills = ({ items, value, onChange, testId = 'filter-pills', size = 'md' }) => (
  <div className={`kit-pills is-${size}`} role="tablist" data-testid={testId}>
    {items.filter(Boolean).map((it) => {
      if (it.options) {
        const on = it.options.some((o) => o.key === value);
        const current = it.options.find((o) => o.key === value);
        return <label key={it.key} className={`kit-pill kit-pill-select ${on ? 'is-on' : ''}`} data-testid={`${testId}-${it.key}`}>
          <span>{current ? current.label : it.label}</span><ChevronDown size={14} />
          <Select360 value={on ? value : ''} onChange={(e) => onChange(e.target.value)} aria-label={it.label}>
            <option value="">{typeof it.label === 'string' ? `${it.label} : tous` : 'Tous'}</option>
            {it.options.map((o) => <option key={o.key} value={o.key}>{typeof o.label === 'string' ? o.label : (o.text ?? o.key)}</option>)}
          </Select360>
        </label>;
      }
      const on = value === it.key;
      return <button key={it.key} type="button" role="tab" aria-selected={on} className={`kit-pill ${on ? 'is-on' : ''}`} onClick={() => onChange(it.key)} data-testid={`${testId}-${it.key || 'tous'}`}>
        {it.label}{it.count !== undefined && it.count !== null && <span className="kit-pill-n">{it.count}</span>}
      </button>;
    })}
  </div>
);

// Tag de catégorie / module : pill pastel pleine, texte foncé en gras, sans contour ni ombre. Une couleur par catégorie.
export const CategoryTag = ({ tone = 'sky', children, testId, title }) => (
  <span className={`kit-tag is-${tone}`} title={title} data-testid={testId}>{children}</span>
);

// Chiffre isolé (format Finance) : valeur dans un badge noir arrondi, label en pill colorée dessous, contexte en petit tag gris.
export const StatBadge = ({ value, label, tone = 'mint', hint, tilt = false, testId, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  const ref = usePulseOnChange(value);
  return <Tag ref={ref} type={onClick ? 'button' : undefined} onClick={onClick} className={`kit-stat ${tilt ? 'is-tilt' : ''}`} data-testid={testId}>
    <b className="kit-stat-val">{value}</b>
    {label && <span className={`kit-tag is-${tone}`}>{label}</span>}
    {hint && <small className="kit-stat-hint">{hint}</small>}
  </Tag>;
};

// Fenêtre générique (fond crème, fermeture Escape / clic dehors) — toute liste dense s'ouvre ici plutôt que d'être empilée dans la page.
export const KitModal = ({ open, onClose, title, kicker, children, testId = 'kit-modal', wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(<div className="kit-overlay" onClick={onClose} data-testid={`${testId}-overlay`}>
    <div className={`kit-modal app-da ${wide ? "is-wide" : ""}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} data-testid={testId}>
      <button type="button" className="kit-modal-x" onClick={onClose} aria-label="Fermer" data-testid={`${testId}-close`}><X size={16} /></button>
      {(kicker || title) && <header className="kit-modal-head">{kicker && <span className="kit-kicker">{kicker}</span>}{title && <h3 className="kit-modal-title">{title}<span className="kit-dot">.</span></h3>}</header>}
      {children}
    </div>
  </div>, document.body);
};

// Bandeau compteur : « 6 équipiers sans créneau » sur une ligne → clic = fenêtre avec le détail. Remplace toute liste empilée.
export const CountBanner = ({ count, label, sub, tone = 'gris', onClick, testId = 'count-banner', children, title, kicker, wide = false }) => {
  const [open, setOpen] = React.useState(false);
  const ref = usePulseOnChange(count);
  const click = onClick || (() => setOpen(true));
  return <>
    <button ref={ref} type="button" className={`kit-banner is-${tone}`} onClick={click} data-testid={testId}>
      <span className="kit-banner-n">{count}</span>
      <span className="kit-banner-txt"><b>{label}</b>{sub && <small>{sub}</small>}</span>
      <ChevronRight size={18} className="kit-banner-chev" />
    </button>
    {!onClick && children && <KitModal open={open} onClose={() => setOpen(false)} title={title || label} kicker={kicker} testId={`${testId}-modal`} wide={wide}>{children}</KitModal>}
  </>;
};

// Zéro phrase dans la page : le texte explicatif vit derrière un bouton blanc « Voir l'analyse » (infobulle au survol, fenêtre au clic).
export const InfoButton = ({ text, label = 'Voir l’analyse', by, hook, title, kicker, testId = 'info-btn' }) => {
  const [open, setOpen] = React.useState(false);
  if (!text) return null;
  return <>
    <button type="button" className="kit-info" title={typeof text === 'string' ? text : undefined} onClick={() => setOpen(true)} data-testid={testId}><Info size={16} />{label}</button>
    <KitModal open={open} onClose={() => setOpen(false)} title={title || label} kicker={kicker} testId={`${testId}-modal`}>
      <p className="kit-info-text" data-testid={`${testId}-text`}>{text}</p>
      {(by || hook) && <div className="kit-info-by">{by && <span className="kit-tag is-gris">{by}</span>}{hook && <em>{hook}</em>}</div>}
    </KitModal>
  </>;
};

// Carte « site » : badge noir penché (chiffre ou numéro) · tag pastel (catégorie) · titre serif · tag gris (contexte). Référence absolue pour toute carte chiffrée.
const TONES = ['lilas', 'mint', 'sun', 'rose', 'sky', 'peche'];
export const KpiCard = ({ value, label, sub, title, tone, index = 0, active = false, onClick, testId, children }) => {
  const t = tone || TONES[index % TONES.length];
  const Tag = onClick ? 'button' : 'div';
  const ref = usePulseOnChange(value);
  return <Tag ref={ref} type={onClick ? 'button' : undefined} onClick={onClick} aria-pressed={onClick ? active : undefined} className={`kit-card ${active ? 'is-on' : ''} ${onClick ? 'is-click' : ''}`} data-testid={testId}>
    <span className="kit-card-badge">{value}</span>
    {label && <span className={`kit-tag is-${t} kit-card-tag`}>{label}</span>}
    {title && <b className="kit-card-title">{title}<span className="kit-dot">.</span></b>}
    {sub && <span className="kit-tag is-gris kit-card-sub">{sub}</span>}
    {children}
  </Tag>;
};
